import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { SITE_ORIGIN } from './Seo';
import styles from './Legal.module.css';

// ───────────────────────────────────────────────────────────────
// Legal contact details. Placeholders MUST be filled in before going
// live (German law requires a real postal address and, per BGH since
// 2023, an email address in the Impressum).
// ───────────────────────────────────────────────────────────────
const NAME = 'Amit Edelman';
const ADDRESS_LINE_1 = 'Glockenblumenweg 136';
const ADDRESS_LINE_2 = '12357 Berlin';
const PHONE = '+49 1523 2782970';
const EMAIL = 'amitsurfer80@gmail.com';
const VAT_ID = 'DE43027635318';

function EnglishBody() {
  return (
    <div className={styles.body}>
      <h2 className={styles.h2}>Information according to § 5 DDG</h2>
      <p>
        {NAME}
        <br />
        {ADDRESS_LINE_1}
        <br />
        {ADDRESS_LINE_2}
      </p>

      <h2 className={styles.h2}>Contact</h2>
      <p>
        Phone: {PHONE}
        <br />
        Email: {EMAIL}
      </p>

      <h2 className={styles.h2}>VAT identification number</h2>
      <p>
        VAT identification number according to § 27a of the German Value Added Tax Act:
        <br />
        {VAT_ID}
      </p>

      <h2 className={styles.h2}>Profession and professional regulations</h2>
      <p>
        Profession: Freelance videographer and video editor (Freiberufler)
        <br />
        Conferred in: Federal Republic of Germany
        <br />
        This is a freelance activity. There is no registration in a commercial
        register or a comparable register.
      </p>

      <h2 className={styles.h2}>Responsible for the content</h2>
      <p>
        Responsible for the content according to § 18 Abs. 2 MStV: {NAME},
        address as above.
      </p>

      <h2 className={styles.h2}>Liability for contents</h2>
      <p>
        As a service provider, I am responsible for my own content on these
        pages in accordance with § 7 Abs. 1 DDG and the general laws. According
        to §§ 8 to 10 DDG, however, I am not obliged to monitor transmitted or
        stored third-party information or to investigate circumstances that
        indicate illegal activity. Obligations to remove or block the use of
        information under the general laws remain unaffected. Liability for this
        is only possible from the time of knowledge of a concrete legal
        violation. Upon becoming aware of such violations, I will remove this
        content immediately.
      </p>

      <h2 className={styles.h2}>Liability for links</h2>
      <p>
        This offer contains links to external third-party websites over whose
        content I have no influence. Therefore I cannot accept any liability for
        this third-party content. The respective provider or operator of the
        linked pages is always responsible for the content of the linked pages.
        The linked pages were checked for possible legal violations at the time
        of linking. Illegal content was not recognizable at the time of linking.
        A permanent content control of the linked pages is not reasonable without
        concrete evidence of a legal violation. If I become aware of legal
        violations, I will remove such links immediately.
      </p>

      <h2 className={styles.h2}>Copyright</h2>
      <p>
        The content and works created by the site operator on these pages are
        subject to German copyright law. The duplication, editing, distribution
        and any form of commercialization of such material beyond the bounds of
        copyright require the written consent of its respective author or
        creator. Downloads and copies of this page are only permitted for
        private use, not for commercial use. Insofar as the content on this page
        was not created by the operator, the copyrights of third parties are
        respected. In particular, third-party content is identified as such.
        Should you nevertheless become aware of a copyright infringement, please
        let me know. If I become aware of legal violations, I will remove such
        content immediately.
      </p>

      <h2 className={styles.h2}>Dispute resolution</h2>
      <p>
        The European Commission provides a platform for online dispute
        resolution (ODR):{' '}
        <a
          href="https://ec.europa.eu/consumers/odr/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://ec.europa.eu/consumers/odr/
        </a>
        . You can find my email address above in the Impressum. I am not
        obliged and not willing to participate in dispute resolution proceedings
        before a consumer arbitration board.
      </p>
    </div>
  );
}

function GermanBody() {
  return (
    <div className={styles.body}>
      <h2 className={styles.h2}>Angaben gemäß § 5 DDG</h2>
      <p>
        {NAME}
        <br />
        {ADDRESS_LINE_1}
        <br />
        {ADDRESS_LINE_2}
      </p>

      <h2 className={styles.h2}>Kontakt</h2>
      <p>
        Telefon: {PHONE}
        <br />
        E-Mail: {EMAIL}
      </p>

      <h2 className={styles.h2}>Umsatzsteuer-Identifikationsnummer</h2>
      <p>
        Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz:
        <br />
        {VAT_ID}
      </p>

      <h2 className={styles.h2}>Berufsbezeichnung und berufsrechtliche Regelungen</h2>
      <p>
        Berufsbezeichnung: Freiberuflicher Videograf und Video-Editor
        (Freiberufler)
        <br />
        Verliehen in: Bundesrepublik Deutschland
        <br />
        Es handelt sich um eine freiberufliche Tätigkeit. Es besteht keine
        Eintragung in ein Handelsregister oder ein vergleichbares Register.
      </p>

      <h2 className={styles.h2}>Verantwortlich für den Inhalt</h2>
      <p>
        Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV: {NAME},
        Anschrift wie oben.
      </p>

      <h2 className={styles.h2}>Haftung für Inhalte</h2>
      <p>
        Als Diensteanbieter bin ich gemäß § 7 Abs. 1 DDG für eigene Inhalte
        auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach
        §§ 8 bis 10 DDG bin ich jedoch nicht verpflichtet, übermittelte oder
        gespeicherte fremde Informationen zu überwachen oder nach Umständen
        zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
        Verpflichtungen zur Entfernung oder Sperrung der Nutzung von
        Informationen nach den allgemeinen Gesetzen bleiben hiervon
        unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem
        Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei
        Bekanntwerden von entsprechenden Rechtsverletzungen werde ich diese
        Inhalte umgehend entfernen.
      </p>

      <h2 className={styles.h2}>Haftung für Links</h2>
      <p>
        Dieses Angebot enthält Links zu externen Websites Dritter, auf deren
        Inhalte ich keinen Einfluss habe. Deshalb kann ich für diese fremden
        Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten
        Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten
        verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der
        Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige
        Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine
        permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch
        ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar.
        Bei Bekanntwerden von Rechtsverletzungen werde ich derartige
        Verlinkungen umgehend entfernen.
      </p>

      <h2 className={styles.h2}>Urheberrecht</h2>
      <p>
        Die durch den Seitenbetreiber erstellten Inhalte und Werke auf
        diesen Seiten unterliegen dem deutschen Urheberrecht. Die
        Vervielfältigung, Bearbeitung, Verbreitung und jede Art der
        Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der
        schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
        Downloads und Kopien dieser Seite sind nur für den privaten Gebrauch,
        nicht aber für die kommerzielle Nutzung gestattet. Soweit die
        Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden
        die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte
        Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine
        Urheberrechtsverletzung aufmerksam werden, bitte ich um einen
        entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen
        werde ich derartige Inhalte umgehend entfernen.
      </p>

      <h2 className={styles.h2}>Streitschlichtung</h2>
      <p>
        Die Europäische Kommission stellt eine Plattform zur
        Online-Streitbeilegung (OS) bereit:{' '}
        <a
          href="https://ec.europa.eu/consumers/odr/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://ec.europa.eu/consumers/odr/
        </a>
        . Meine E-Mail-Adresse finden Sie oben im Impressum. Ich bin nicht
        verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    </div>
  );
}

export default function Impressum() {
  const { lang, buildPath, tr } = useLang();
  const isDe = lang === 'de';

  const title = isDe ? 'Impressum | EDELNAD Productions' : 'Impressum (Legal notice) | EDELNAD Productions';
  const description = isDe
    ? 'Impressum und Anbieterkennzeichnung gemäß § 5 DDG: Amit Edelman, EDELNAD Productions, Berlin. Kontakt, USt-IdNr. und Haftungshinweise.'
    : 'Legal notice / Impressum according to § 5 DDG: Amit Edelman, EDELNAD Productions, Berlin. Contact, VAT ID and liability notices.';
  const langPath = '/impressum';
  const canonical = `${SITE_ORIGIN}${isDe ? '/de' : ''}${langPath}`;

  return (
    <section className={styles.section}>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${SITE_ORIGIN}${langPath}`} />
        <link rel="alternate" hrefLang="de" href={`${SITE_ORIGIN}/de${langPath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}${langPath}`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="EDELNAD Productions" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={isDe ? 'de_DE' : 'en_US'} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <div className={`container ${styles.inner}`}>
        <Link to={buildPath('/')} className={styles.back}>
          {tr('← Back to home', '← Zurück zur Startseite')}
        </Link>

        <p className="section-label">{tr('Legal', 'Impressum')}</p>
        <h1 className={styles.title}>Impressum</h1>

        <div className={styles.address}>
          <p>{NAME}</p>
          <p>{ADDRESS_LINE_1}</p>
          <p>{ADDRESS_LINE_2}</p>
        </div>

        {isDe ? <GermanBody /> : <EnglishBody />}

        <nav className={styles.footNav} aria-label="Legal navigation">
          <Link to={buildPath('/')} className={styles.footLink}>
            {tr('← Home', '← Start')}
          </Link>
          <Link to={buildPath('/datenschutz')} className={styles.footLink}>
            {tr('Datenschutz →', 'Datenschutz →')}
          </Link>
        </nav>
      </div>
    </section>
  );
}
